// react and js
import PropTypes from "prop-types";
import * as Yup from "yup";
import { React, useState, useRef, useEffect, useContext } from "react";
import { useFormik, FormikProvider } from "formik";
import { useTranslation } from "react-i18next";

// mui
import {
  Stack,
  Box,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";

// components
import ModalDialog from "../../../components/ModalDialog";
import RequestMessage from "../../../components/RequestMessage";
import Iconify from "../../../components/Iconify";
import backendRequest from "../../../components/BackendRequest";
import CountrySelect from "../../../components/CountrySelect";

// hooks
import appContext from "../../../hooks/appContext";

// layouts

// pages

// theme
import {
  B3Small,
  ModalContentSwitch,
  ModalContentTextField,
  ModalActionsLoadingButton,
} from "../../../theme/styled";

// utils
import { combineReturnMessage, TranslatedMessage } from "../../../utils/common";

// widgets

// ----------------------------------------------------------------------
// Code
// ----------------------------------------------------------------------
function RequestBackendAction(
  setSubmitting,
  setError,
  setSuccess,
  setWarning,
  setReturnMessage,
  postRequestUpdate,
  SubmitRequestContent
) {
  if (localStorage.getItem("myReauthentication") === "false" && navigator.onLine === true) {
    setError(false);
    setSuccess(false);
    setWarning(false);
    setReturnMessage("");

    backendRequest(process.env.REACT_APP_URL_API_DASHBOARD, SubmitRequestContent)
      .then((contentFromBackend) => {
        const keyOnly = Object.keys(contentFromBackend);
        const checkKey = keyOnly.includes("message");

        if (checkKey === true) {
          if (contentFromBackend.message === "success") {
            setSuccess(true);
            combineReturnMessage(contentFromBackend, setReturnMessage);
            postRequestUpdate(750);
          } else {
            setWarning(true);
            combineReturnMessage(contentFromBackend, setReturnMessage);
          }
        } else {
          setWarning(true);
          combineReturnMessage(contentFromBackend, setReturnMessage);
        }
        setSubmitting(false);
      })
      .catch(() => {
        if(navigator.onLine === true) {
        setReturnMessage(<TranslatedMessage tKey="distributorPortal.common.unknownError" />);
        setError(true);
        setSubmitting(false); }
      });
  }
}

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export default function HandleDistributorPortalAddNewDistributionCustomerTickData({
  distributor,
  planId,
}) {
  // ----------------------------------------------------------------------
  // Properties
  // ----------------------------------------------------------------------
  HandleDistributorPortalAddNewDistributionCustomerTickData.propTypes = {
    distributor: PropTypes.any.isRequired,
    planId: PropTypes.any.isRequired,
  };

  const { t } = useTranslation();
  const { stateStorage, setStateStorage } = useContext(appContext);

  const [open, setOpen] = useState(false);
  const handleClose = () => setOpen(false);

  const wrapperRef = useRef(null);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [warning, setWarning] = useState(false);
  const [returnMessage, setReturnMessage] = useState("");

  const postRequestUpdate = (autoClose) => {
    setTimeout(() => {
      handleClose();
      setStateStorage((previousState) => ({
        ...previousState,
        layoutUpdateFeatures: true,
        widgetUpdateDistributorPortalTickDataDistributionCustomers: true,
        widgetUpdateDistributorPortalManageTickDataDistributionCustomer: true,
        widgetUpdateDistributorPortalContingents: true,
      }));
      setError(false);
      setSuccess(false);
      setWarning(false);
    }, autoClose);
  };

  const [formSendPasswordReset, setFormSendPasswordReset] = useState(true);
  const [formSendApprovalEmail, setFormSendApprovalEmail] = useState(false);

  const [formMarketingShares, setFormMarketingShares] = useState(true);
  const [formTailorizedAdds, setFormTailorizedAdds] = useState(true);
  const [formEmailNewsletter, setFormEmailNewsletter] = useState(true);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setError(false);
        setWarning(false);
        setSuccess(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside, {passive: false, capture: false});
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [wrapperRef]);

  const myValidationScheme = Yup.object().shape({
    email: Yup.string()
      .email(t("distributorPortal.validation.emailInvalid"))
      .required(t("distributorPortal.validation.emailRequired")),
    password: Yup.string()
      .required(t("distributorPortal.validation.passwordRequired"))
      .min(8, t("distributorPortal.validation.passwordMin"))
      .max(64, t("distributorPortal.validation.passwordMax"))
      .matches(/[0-9]/, t("distributorPortal.validation.passwordNumber"))
      .matches(/[a-z]/, t("distributorPortal.validation.passwordLowercase"))
      .matches(/[A-Z]/, t("distributorPortal.validation.passwordUppercase")),
    forename: Yup.string(),
    surname: Yup.string(),
    province: Yup.string(),
    city: Yup.string(),
    postal_code: Yup.string(),
    street: Yup.string(),
  });

  const generatePassword = (
    length = 24,
    wishlist = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz~!@-#$"
  ) =>
    Array.from(crypto.getRandomValues(new Uint32Array(length)))
      .map((x) => wishlist[x % wishlist.length])
      .join("");

  const formik = useFormik({
    initialValues: {
      email: "",
      password: generatePassword(),
      forename: "",
      surname: "",
      country: null,
      province: "",
      city: "",
      postal_code: "",
      street: "",
    },
    validationSchema: myValidationScheme,
    onSubmit: (values, { setSubmitting }) => {
      const SubmitRequestContent = {
        request_type: "dashboard",
        route_info: "distributor_portal",
        widget: "customer_and_account_management",
        operation: "add_new_distribution_customer",
        client: {
          dashboard_access_token:
            stateStorage.userInformation.answer.dashboard_access_token,
        },
        admin: {
          selected_distributor_id: distributor,
        },
        used_plan_id: planId,
        send_password_reset_email: formSendPasswordReset,
        send_approval_email: formSendApprovalEmail,
        dashboard: {
          email: values.email,
          password: values.password,
          forename: values.forename,
          surname: values.surname,
          marketing_share: formMarketingShares ? "1" : "0",
          tailorized_adds: formTailorizedAdds ? "1" : "0",
          email_notification_settings: `newsletter=${
            formEmailNewsletter ? "1" : "0"
          },`,
          country: values.country === null ? "" : values.country,
          province: values.province,
          city: values.city,
          postal_code: values.postal_code,
          street: values.street,
        },
        accounts: [
          {
            operation_id: "1",
            type: "mt5_tick_data",
          },
        ],
      };

      RequestBackendAction(
        setSubmitting,
        setError,
        setSuccess,
        setWarning,
        setReturnMessage,
        postRequestUpdate,
        SubmitRequestContent
      );
    },
  });

  const { errors, touched, values, isSubmitting, handleSubmit, getFieldProps } =
    formik;

  const descriptionElementRef = useRef(null);
  useEffect(() => {
    if (open) {
      const { current: descriptionElement } = descriptionElementRef;
      if (descriptionElement !== null) {
        descriptionElement.focus();
      }
    }
  }, [open]);

  const [scroll, setScroll] = useState("paper");

  const handleClickOpen = (scrollType) => () => {
    setOpen(true);
    setScroll(scrollType);

    // Reinit form values when reopened
    setFormSendPasswordReset(true);
    setFormSendApprovalEmail(false);

    setFormMarketingShares(true);
    setFormTailorizedAdds(true);
    setFormEmailNewsletter(true);

	  formik.values.email = "";
	  formik.values.password = generatePassword();
	  formik.values.forename = "";
	  formik.values.surname = "";
	  formik.values.country = null;
	  formik.values.province = "";
	  formik.values.city = "";
	  formik.values.postal_code = "";
	  formik.values.street = "";
  };
  const handleClickClose = () => {
    setOpen(false);
  };

  return (stateStorage.userInformation.account_settings.config_data.config_data_distributor.manage_accounts === "1" ?
    <>
      <B3Small fullWidth variant="outlined" onClick={handleClickOpen("paper")}>
        {t("distributorPortal.tickData.actions.addNewCustomer")}
      </B3Small>

      <ModalDialog
        open={open}
        handleClose={handleClose}
        scroll={scroll}
        outlet={
          <>
            <DialogTitle
              id="dialog-title"
              sx={{
                background: "#F2F2F2",
                textTransform: "none",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                alignContent: "center",
                py: 1,
                pr: 1,
              }}
            >
              <Typography variant="contentBig">
                {t("distributorPortal.tickData.addCustomerDialog.title")}
              </Typography>

              <IconButton
                onClick={handleClickClose}
                sx={{ borderRadius: "10px" }}
              >
                <Iconify
                  icon="eva:close-outline"
                  minWidth={25}
                  minHeight={25}
                  color="#2E2E2E"
                />
              </IconButton>
            </DialogTitle>

            <FormikProvider value={formik}>
              <DialogContent
                dividers
                sx={{
                  background: "#F2F2F2",
                  borderTop: "1px solid #C8C8C8",
                  borderBottom: "0px solid #C8C8C8",
                }}
              >
                <Stack spacing={1}>
                  <Box>
                    <Typography
                      variant="contentSmallBold"
                      sx={{
                        color:
                          Boolean(touched.email && errors.email) === true
                            ? "red"
                            : "none",
                      }}
                    >
                      {Boolean(touched.email && errors.email) === true
                        ? t("distributorPortal.customer.fields.emailInvalid")
                        : t("distributorPortal.customer.fields.email")}
                    </Typography>
                    <ModalContentTextField
                      fullWidth
                      size="small"
                      type="text"
                      {...getFieldProps("email")}
                      error={Boolean(touched.email && errors.email)}
                    />
                  </Box>

                  <Box>
                    <Typography
                      variant="contentSmallBold"
                      sx={{
                        color:
                          Boolean(touched.password && errors.password) === true
                            ? "red"
                            : "none",
                      }}
                    >
                      {Boolean(touched.password && errors.password) === true
                        ? errors.password
                        : t("distributorPortal.customer.fields.password")}
                    </Typography>
                    <ModalContentTextField
                      fullWidth
                      size="small"
                      type="text"
                      {...getFieldProps("password")}
                      error={Boolean(touched.password && errors.password)}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      alignContent: "center",
                      py: 2,
                    }}
                  >
                    <Typography variant="contentSmall">
                      {t("distributorPortal.customer.fields.sendPasswordResetEmail")}
                    </Typography>
                    <ModalContentSwitch
                      checked={formSendPasswordReset}
                      onChange={(event) => {
                        setFormSendPasswordReset(event.target.checked);
                      }}
                      inputProps={{ "aria-label": "controlled" }}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      alignContent: "center",
                      pb: 2,
                    }}
                  >
                    <Typography variant="contentSmall">
                      {t("distributorPortal.customer.fields.sendApprovalEmail")}
                    </Typography>
                    <ModalContentSwitch
                      checked={formSendApprovalEmail}
                      onChange={(event) => {
                        setFormSendApprovalEmail(event.target.checked);
                      }}
                      inputProps={{ "aria-label": "controlled" }}
                    />
                  </Box>

                  <Box>
                    <Typography
                      variant="contentSmallBold"
                      sx={{
                        color:
                          Boolean(touched.forename && errors.forename) === true
                            ? "red"
                            : "none",
                      }}
                    >
                      {Boolean(touched.forename && errors.forename) === true
                        ? t("distributorPortal.validation.forenameRequired")
                        : t("distributorPortal.customer.fields.forename")}
                    </Typography>
                    <ModalContentTextField
                      fullWidth
                      size="small"
                      type="text"
                      {...getFieldProps("forename")}
                      error={Boolean(touched.forename && errors.forename)}
                    />
                  </Box>

                  <Box>
                    <Typography
                      variant="contentSmallBold"
                      sx={{
                        color:
                          Boolean(touched.surname && errors.surname) === true
                            ? "red"
                            : "none",
                      }}
                    >
                      {Boolean(touched.surname && errors.surname) === true
                        ? t("distributorPortal.validation.surnameRequired")
                        : t("distributorPortal.customer.fields.surname")}
                    </Typography>
                    <ModalContentTextField
                      fullWidth
                      size="small"
                      type="text"
                      {...getFieldProps("surname")}
                      error={Boolean(touched.surname && errors.surname)}
                    />
                  </Box>

                  <Box>
                    <Typography
                      variant="contentSmallBold"
                      sx={{
                        color:
                          Boolean(touched.street && errors.street) === true
                            ? "red"
                            : "none",
                      }}
                    >
                      {Boolean(touched.street && errors.street) === true
                        ? t("distributorPortal.validation.streetRequired")
                        : t("distributorPortal.customer.fields.street")}
                    </Typography>
                    <ModalContentTextField
                      fullWidth
                      size="small"
                      type="text"
                      {...getFieldProps("street")}
                      error={Boolean(touched.street && errors.street)}
                    />
                  </Box>

                  <Box>
                    <Typography
                      variant="contentSmallBold"
                      sx={{
                        color:
                          Boolean(touched.postal_code && errors.postal_code) ===
                          true
                            ? "red"
                            : "none",
                      }}
                    >
                      {Boolean(touched.postal_code && errors.postal_code) ===
                      true
                        ? t("distributorPortal.validation.postalCodeRequired")
                        : t("distributorPortal.customer.fields.postalCode")}
                    </Typography>
                    <ModalContentTextField
                      fullWidth
                      size="small"
                      type="text"
                      {...getFieldProps("postal_code")}
                      error={Boolean(touched.postal_code && errors.postal_code)}
                    />
                  </Box>

                  <Box>
                    <Typography
                      variant="contentSmallBold"
                      sx={{
                        color:
                          Boolean(touched.city && errors.city) === true
                            ? "red"
                            : "none",
                      }}
                    >
                      {Boolean(touched.city && errors.city) === true
                        ? t("distributorPortal.validation.cityRequired")
                        : t("distributorPortal.customer.fields.city")}
                    </Typography>
                    <ModalContentTextField
                      fullWidth
                      size="small"
                      type="text"
                      {...getFieldProps("city")}
                      error={Boolean(touched.city && errors.city)}
                    />
                  </Box>

                  <Box>
                    <Typography
                      variant="contentSmallBold"
                      sx={{
                        color:
                          Boolean(touched.province && errors.province) === true
                            ? "red"
                            : "none",
                      }}
                    >
                      {Boolean(touched.province && errors.province) === true
                        ? t("distributorPortal.validation.provinceRequired")
                        : t("distributorPortal.customer.fields.provinceOptional")}
                    </Typography>
                    <ModalContentTextField
                      fullWidth
                      size="small"
                      type="text"
                      {...getFieldProps("province")}
                      error={Boolean(touched.province && errors.province)}
                    />
                  </Box>

                  <Box>
                    <Typography variant="contentSmallBold">{t("distributorPortal.customer.fields.country")}</Typography>
                    <CountrySelect
                      error={Boolean(touched.country && errors.country)}
                      initCountry={null}
                      setFormCountry={(newValue) => {
                        values.country = newValue;
                      }}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      alignContent: "center",
                      py: 2,
                    }}
                  >
                    <Typography variant="contentSmall">
                      {t("distributorPortal.customer.fields.usePersonalDataMarketing")}
                    </Typography>
                    <ModalContentSwitch
                      checked={formMarketingShares}
                      onChange={(event) => {
                        setFormMarketingShares(event.target.checked);
                      }}
                      inputProps={{ "aria-label": "controlled" }}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      alignContent: "center",
                      pb: 2,
                    }}
                  >
                    <Typography variant="contentSmall">
                      {t("distributorPortal.customer.fields.usePersonalDataTailorizedAdds")}
                    </Typography>
                    <ModalContentSwitch
                      checked={formTailorizedAdds}
                      onChange={(event) => {
                        setFormTailorizedAdds(event.target.checked);
                      }}
                      inputProps={{ "aria-label": "controlled" }}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      alignContent: "center",
                      pb: 2,
                    }}
                  >
                    <Typography variant="contentSmall">{t("distributorPortal.customer.fields.newsletter")}</Typography>
                    <ModalContentSwitch
                      checked={formEmailNewsletter}
                      onChange={(event) => {
                        setFormEmailNewsletter(event.target.checked);
                      }}
                      inputProps={{ "aria-label": "controlled" }}
                    />
                  </Box>
                </Stack>
              </DialogContent>

              <DialogActions
                sx={{
                  background: "#F2F2F2",
                  p: 0,
                  display: "flex",
                  alignItems: "flex-start",
                  flexDirection: "column",
                }}
              >
                <RequestMessage
                  wrapperRef={wrapperRef}
                  error={error}
                  warning={warning}
                  success={success}
                  returnMessage={returnMessage}
                />

                <ModalActionsLoadingButton
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                  onClick={handleSubmit}
                  loading={isSubmitting}
                >
                  {t("distributorPortal.common.add")}
                </ModalActionsLoadingButton>
              </DialogActions>
            </FormikProvider>
          </>
        }
      />
    </> : null
  );
}
